/**
 * Set Finding Disposition Modal
 * Unified modal for setting lifecycle dispositions on findings
 * Simplified UX with status-driven disposition selection
 */
import React, { useState, useEffect } from 'react';
import {
  Modal,
  Form,
  Select,
  Input,
  Checkbox,
  Radio,
  Space,
  Typography,
  Row,
  Col,
  Spin,
  Divider
} from 'antd';
import { showError, showSuccess } from '../../../utils/notifications';
import {
  getDispositions,
  getWaitUntilTimeframes,
  getSuppressionReasons,
  bulkSetFindingDisposition
} from '../api/trustgridApi';

const { Option } = Select;
const { TextArea } = Input;
const { Text, Title } = Typography;

function SetFindingDispositionModal({
  visible,
  findingIds = [],
  onClose,
  onSuccess
}) {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [dispositionsData, setDispositionsData] = useState({ in_progress: [], closed: [] });
  const [timeframes, setTimeframes] = useState([]);
  const [suppressionReasons, setSuppressionReasons] = useState([]);
  const [selectedStatus, setSelectedStatus] = useState(null);
  const [selectedDispositions, setSelectedDispositions] = useState([]);
  const [showWaitUntil, setShowWaitUntil] = useState(false);
  const [showSuppressionReasons, setShowSuppressionReasons] = useState(false);

  useEffect(() => {
    if (visible) {
      fetchConfigData();
    }
  }, [visible]);

  const fetchConfigData = async () => {
    setLoading(true);
    try {
      const [dispositionsRes, timeframesRes, reasonsRes] = await Promise.all([
        getDispositions(),
        getWaitUntilTimeframes(),
        getSuppressionReasons()
      ]);

      setDispositionsData(dispositionsRes.data);
      setTimeframes(timeframesRes.data.timeframes || []);
      setSuppressionReasons(reasonsRes.data.reasons || []);
    } catch (error) {
      console.error('Failed to fetch config data:', error);
      showError(error);
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = (status) => {
    setSelectedStatus(status);
    setSelectedDispositions([]);
    setShowWaitUntil(false);
    setShowSuppressionReasons(false);

    // Clear disposition-dependent fields
    form.setFieldsValue({
      dispositions: [],
      disposition_comment: undefined,
      wait_until_timeframe_id: undefined,
      suppression_reasons: undefined
    });
  };

  const handleDispositionsChange = (checkedValues) => {
    setSelectedDispositions(checkedValues);

    const availableDispositions = getAvailableDispositions();

    // Show Wait Until if any selected disposition is time-bound (from config)
    const needsWaitUntil = checkedValues.some(dispositionName => {
      const config = availableDispositions.find(d => d.disposition_name === dispositionName);
      return config?.is_time_bound;
    });
    setShowWaitUntil(needsWaitUntil);

    // Show Suppression Reasons if Suppressed disposition is selected
    const needsSuppressionReasons = checkedValues.some(d =>
      d.toLowerCase().includes('suppress')
    );
    setShowSuppressionReasons(needsSuppressionReasons);

    // Clear fields if not needed
    if (!needsWaitUntil) {
      form.setFieldsValue({ wait_until_timeframe_id: undefined });
    }
    if (!needsSuppressionReasons) {
      form.setFieldsValue({ suppression_reasons: undefined });
    }
  };

  const getAvailableDispositions = () => {
    if (!selectedStatus) return [];
    return selectedStatus === 'open' ? dispositionsData.in_progress : dispositionsData.closed;
  };

  const isCommentRequired = () => {
    const availableDispositions = getAvailableDispositions();
    return selectedDispositions.some(dispositionName => {
      const config = availableDispositions.find(d => d.disposition_name === dispositionName);
      return config?.requires_comment;
    });
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();

      // For now, use the first selected disposition (bulk API expects single disposition)
      const primaryDisposition = values.dispositions?.[0];

      if (!primaryDisposition) {
        showError('Please select at least one disposition');
        return;
      }

      setLoading(true);

      // Combine suppression reasons into a comma-separated string
      const suppressionReason = values.suppression_reasons?.join(', ');

      await bulkSetFindingDisposition(
        findingIds,
        primaryDisposition,
        values.disposition_comment,
        values.wait_until_timeframe_id,
        suppressionReason
      );

      showSuccess(`Disposition set on ${findingIds.length} finding(s)`);

      form.resetFields();
      setSelectedStatus(null);
      setSelectedDispositions([]);
      setShowWaitUntil(false);
      setShowSuppressionReasons(false);

      if (onSuccess) {
        onSuccess();
      }

      onClose();
    } catch (error) {
      console.error('Failed to set disposition:', error);
      showError(error);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    form.resetFields();
    setSelectedStatus(null);
    setSelectedDispositions([]);
    setShowWaitUntil(false);
    setShowSuppressionReasons(false);
    onClose();
  };

  return (
    <Modal
      title={
        <div>
          <div style={{ fontSize: '18px', fontWeight: 600, marginBottom: '4px' }}>
            Set Disposition for {findingIds.length} Finding{findingIds.length !== 1 ? 's' : ''}
          </div>
          <Text type="secondary" style={{ fontSize: '12px', fontWeight: 'normal' }}>
            These updates will be tracked in the audit log
          </Text>
        </div>
      }
      open={visible}
      onOk={handleSubmit}
      onCancel={handleCancel}
      okText="Save"
      cancelText="Cancel"
      confirmLoading={loading}
      width={800}
      destroyOnClose
    >
      <Spin spinning={loading}>
        <Form
          form={form}
          layout="vertical"
          initialValues={{}}
        >
          <Row gutter={24}>
            {/* Left Column */}
            <Col span={showWaitUntil ? 14 : 24}>
              {/* Status */}
              <Form.Item
                name="status"
                label={<Text strong>Status: <Text type="danger">*</Text></Text>}
                rules={[{ required: true, message: 'Please select a status' }]}
              >
                <Select
                  placeholder="Select status"
                  onChange={handleStatusChange}
                  size="large"
                >
                  <Option value="open">Open</Option>
                  <Option value="closed">Closed</Option>
                </Select>
              </Form.Item>

              {/* Disposition */}
              {selectedStatus && (
                <Form.Item
                  name="dispositions"
                  label={
                    <Text strong>
                      Disposition: {isCommentRequired() && <Text type="danger">*</Text>}
                    </Text>
                  }
                  rules={[
                    { required: true, message: 'Please select at least one disposition' }
                  ]}
                >
                  <Checkbox.Group
                    style={{ width: '100%' }}
                    onChange={handleDispositionsChange}
                  >
                    <Space direction="vertical" size="small" style={{ width: '100%' }}>
                      {getAvailableDispositions().map(disposition => (
                        <Checkbox key={disposition.id} value={disposition.disposition_name}>
                          {disposition.disposition_name}
                        </Checkbox>
                      ))}
                    </Space>
                  </Checkbox.Group>
                </Form.Item>
              )}

              {/* Suppression Reasons (if Suppress is selected) */}
              {showSuppressionReasons && suppressionReasons.length > 0 && (
                <Form.Item
                  name="suppression_reasons"
                  label={<Text strong>Suppression Reason(s): <Text type="danger">*</Text></Text>}
                  rules={[{ required: true, message: 'Please select at least one suppression reason' }]}
                >
                  <Checkbox.Group style={{ width: '100%' }}>
                    <Space direction="vertical" size="small" style={{ width: '100%' }}>
                      {suppressionReasons.map(reason => (
                        <Checkbox key={reason.id} value={reason.reason_name}>
                          {reason.reason_name}
                        </Checkbox>
                      ))}
                    </Space>
                  </Checkbox.Group>
                </Form.Item>
              )}

              {/* Comment */}
              <Form.Item
                name="disposition_comment"
                label={
                  <Text strong>
                    Comment: {isCommentRequired() && <Text type="danger">*</Text>}
                  </Text>
                }
                rules={[
                  {
                    required: isCommentRequired(),
                    message: 'Comment is required for the selected disposition'
                  }
                ]}
              >
                <TextArea
                  rows={4}
                  placeholder="Enter comment..."
                  maxLength={1000}
                  showCount
                />
              </Form.Item>
            </Col>

            {/* Right Column - Wait Until */}
            {showWaitUntil && (
              <Col span={10}>
                <div style={{
                  backgroundColor: '#f5f5f5',
                  padding: '16px',
                  borderRadius: '4px',
                  height: '100%'
                }}>
                  <Form.Item
                    label={<Text strong>Wait before alerting again ...</Text>}
                  >
                    <Checkbox.Group style={{ width: '100%' }}>
                      <Space direction="vertical" size="small" style={{ width: '100%' }}>
                        <Radio.Group
                          onChange={(e) => form.setFieldsValue({ wait_until_timeframe_id: e.target.value })}
                        >
                          <Space direction="vertical" size="small">
                            {timeframes.map(tf => (
                              <Radio key={tf.id} value={tf.id}>
                                {tf.label}
                                {tf.preview_date && (
                                  <Text type="secondary" style={{ fontSize: '11px', marginLeft: 8 }}>
                                    (Until: {new Date(tf.preview_date).toLocaleDateString()})
                                  </Text>
                                )}
                              </Radio>
                            ))}
                          </Space>
                        </Radio.Group>
                      </Space>
                    </Checkbox.Group>
                  </Form.Item>
                  <Form.Item name="wait_until_timeframe_id" hidden>
                    <Input />
                  </Form.Item>
                </div>
              </Col>
            )}
          </Row>
        </Form>
      </Spin>
    </Modal>
  );
}

export default SetFindingDispositionModal;
